/**
 * AS 8 VISÕES DE VENDA (§7.5).
 *
 * Uma visão NÃO é uma tela nova. É filtro + agrupamento salvos sobre a MESMA
 * fonte de `/reports/ordens` — a mesma linha de pedido, olhada de outro ângulo.
 * Foi assim que a §7.5 as descreveu, e é por isso que elas cabem num arquivo de
 * dados em vez de oito componentes.
 *
 * A visão escolhida vai para a QUERY STRING da URL (`?visao=mix`), então o
 * link é compartilhável: o dono manda "olha o mix de terça" para o gerente e o
 * gerente abre exatamente a mesma tela. Criar/persistir visão DO USUÁRIO não
 * está no escopo deste card (PREMISSA 11).
 */

export interface VisaoDeVenda {
  id: string;
  titulo: string;
  /** A pergunta de negócio que a visão responde. Vai na tela, como subtítulo. */
  pergunta: string;
  /** Dimensões do `agruparPor`, separadas por vírgula na chamada. */
  agruparPor: string[];
  /** Colunas em destaque; as outras continuam disponíveis no seletor. */
  colunas: string[];
  /** Filtro fixo da visão (ex.: delivery só olha delivery). */
  filtro?: { canal?: string; status?: string };
  /** Aviso que a visão SEMPRE mostra, porque é conhecido e não some com filtro. */
  avisoConhecido?: string;
  /** Visão que existe mas não pode ser usada — com o motivo escrito. */
  desabilitada?: string;
}

export const VISOES_DE_VENDA: VisaoDeVenda[] = [
  {
    id: "periodo",
    titulo: "Venda por período",
    pergunta: "Quanto vendi por dia?",
    agruparPor: ["dia"],
    colunas: [
      "dia",
      "quantidade",
      "receitaBruta",
      "descontos",
      "receitaLiquida",
      "numeroDePedidos",
      "ticket",
    ],
  },
  {
    id: "horaXdia",
    titulo: "Hora × dia da semana",
    pergunta: "Que horas a casa enche, e em que dia?",
    agruparPor: ["diaDaSemana", "hora"],
    colunas: [
      "diaDaSemana",
      "hora",
      "quantidade",
      "receitaLiquida",
      "numeroDePedidos",
      "numeroDePessoas",
    ],
  },
  {
    id: "mix",
    titulo: "Mix de produtos",
    pergunta: "O que sai mais, e o que sai mais em dinheiro?",
    agruparPor: ["categoria", "produto"],
    colunas: [
      "categoria",
      "produto",
      "quantidade",
      "receitaBruta",
      "receitaLiquida",
    ],
    avisoConhecido:
      'O catálogo ainda não foi saneado: "IF X" e "X" existem como produtos ' +
      "diferentes e esta visão SOMA os dois. O número exato de pares está no " +
      "aviso CATALOGO_IF_NAO_SANEADO abaixo.",
  },
  {
    id: "abc",
    titulo: "Curva ABC",
    pergunta: "Quais 20% dos itens fazem 80% da receita?",
    agruparPor: ["produto"],
    colunas: ["produto", "categoria", "quantidade", "receitaLiquida"],
    avisoConhecido:
      'Mesma ressalva do mix: "IF X" e "X" são somados como um produto só ' +
      "enquanto o catálogo não for saneado. Numa curva ABC isso move item de " +
      "faixa — um produto que parece C pode ser B junto com o gêmeo.",
  },
  {
    id: "ticket",
    titulo: "Ticket médio",
    pergunta: "Quanto cada mesa (ou cada entrega) deixa?",
    agruparPor: ["canal", "dia"],
    colunas: [
      "canal",
      "dia",
      "receitaLiquida",
      "numeroDePedidos",
      "numeroDePessoas",
      "ticket",
    ],
    avisoConhecido:
      "Ticket é POR PESSOA no salão e POR PEDIDO no delivery — são duas contas " +
      "diferentes na mesma coluna, porque é assim que o dono lê cada canal.",
  },
  {
    id: "turno",
    titulo: "Por turno",
    pergunta: "Almoço ou jantar puxa mais?",
    agruparPor: ["turno"],
    colunas: [
      "turno",
      "quantidade",
      "receitaLiquida",
      "numeroDePedidos",
      "ticket",
    ],
  },
  {
    id: "cancelamento",
    titulo: "Cancelamento e desconto",
    pergunta: "Quanto foi cancelado e quanto foi descontado, e onde?",
    agruparPor: ["produto", "status"],
    colunas: [
      "produto",
      "status",
      "quantidade",
      "cancelamentos",
      "descontos",
      "receitaLiquida",
    ],
  },
  {
    id: "delivery",
    titulo: "Delivery",
    pergunta: "Quanto o delivery fatura, e quem entrega?",
    agruparPor: ["entregador", "dia"],
    colunas: [
      "entregador",
      "dia",
      "quantidade",
      "receitaLiquida",
      "taxaEntrega",
      "numeroDePedidos",
      "ticket",
    ],
    filtro: { canal: "3" },
  },
  {
    /**
     * Existe DESABILITADA, e isso é de propósito: a §7.5 previu a visão, e some
     * do menu sem explicação faria parecer esquecimento. Aparece com o motivo.
     */
    id: "atendente",
    titulo: "Comissão por atendente",
    pergunta: "Quem vendeu quanto?",
    agruparPor: [],
    colunas: [],
    desabilitada:
      "O OPS grava o mesmo usuário em todas as linhas de pedido — não existe " +
      "atendente por venda no dado. Mostrar comissão aqui diria que uma pessoa " +
      "vendeu a casa inteira.",
  },
];

export function acharVisao(id?: string | null): VisaoDeVenda | undefined {
  if (!id) return undefined;
  return VISOES_DE_VENDA.find((v) => v.id === id);
}
